import { useCallback, useEffect, useMemo, useRef, useState } from "react"

interface IUseExportGuardProps {
  hasPendingTasks: boolean
  queryParams?: Record<string, unknown>
}

/**
 * Guards against duplicate report generation.
 *
 * - When `queryParams` are provided (components with filters):
 *   - Same filters + pending task → disabled
 *   - Changed filters + pending task → confirmation dialog
 * - When `queryParams` is omitted (no-filter components):
 *   - Any pending task → always disabled
 *
 * Returns:
 * - `isGenerateDisabled` – grey out the generate button
 * - `isConfirmDialogOpen` / close / open – confirmation dialog state
 * - `handleGenerate(triggerExport)` – wraps the trigger with guard logic
 * - `onExportTriggered()` – call after export API succeeds to snapshot params
 */
export const useExportGuard = ({
  hasPendingTasks,
  queryParams,
}: IUseExportGuardProps) => {
  const hasFilters = queryParams !== undefined
  const lastExportParamsRef = useRef<string | null>(null)
  const [filtersChangedSinceExport, setFiltersChangedSinceExport] =
    useState(false)
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false)
  const pendingTriggerRef = useRef<(() => void) | null>(null)

  const serializedParams = useMemo(
    () =>
      hasFilters
        ? JSON.stringify(queryParams, Object.keys(queryParams ?? {}).sort())
        : null,
    [hasFilters, queryParams]
  )

  // Snapshot current params when pending tasks first appear
  useEffect(() => {
    if (hasPendingTasks && lastExportParamsRef.current === null && serializedParams !== null) {
      lastExportParamsRef.current = serializedParams
    }
  }, [hasPendingTasks, serializedParams])

  // Detect filter changes (both directions: changed and reverted back)
  useEffect(() => {
    if (serializedParams !== null && lastExportParamsRef.current !== null) {
      setFiltersChangedSinceExport(
        serializedParams !== lastExportParamsRef.current
      )
    }
  }, [serializedParams])

  const isGenerateDisabled = hasFilters
    ? hasPendingTasks && !filtersChangedSinceExport
    : hasPendingTasks

  const handleCloseConfirmDialog = useCallback(() => {
    setIsConfirmDialogOpen(false)
    pendingTriggerRef.current = null
  }, [])

  const handleConfirm = useCallback(() => {
    setIsConfirmDialogOpen(false)
    pendingTriggerRef.current?.()
    pendingTriggerRef.current = null
  }, [])

  const handleGenerate = useCallback(
    (triggerExport: () => void) => {
      if (hasFilters && hasPendingTasks && filtersChangedSinceExport) {
        pendingTriggerRef.current = triggerExport
        setIsConfirmDialogOpen(true)
      } else {
        triggerExport()
      }
    },
    [hasFilters, hasPendingTasks, filtersChangedSinceExport]
  )

  const onExportTriggered = useCallback(() => {
    if (serializedParams !== null) {
      lastExportParamsRef.current = serializedParams
      setFiltersChangedSinceExport(false)
    }
  }, [serializedParams])

  return {
    isGenerateDisabled,
    isConfirmDialogOpen,
    handleCloseConfirmDialog,
    handleConfirm,
    handleGenerate,
    onExportTriggered,
  }
}
